import { createMutation } from "react-query-kit";
import type { MutationFunction } from "@tanstack/react-query";

import { axiosClient, queryClient } from "@api/clients.ts";
import type { IPostApiEntities } from "@api/apiPath";
import { getApi } from "@api/apiPath/util";

export const createPostFunc = <
  TResponse,
  TPathParams extends object | undefined,
  TData extends object | undefined,
>(
  path: IPostApiEntities,
) => {
  type IVariables = TData extends undefined
    ? {
        pathParam: TPathParams;
      }
    : {
        pathParam: TPathParams;
      } & TData;

  const { key, getPath } = getApi(`${path}.post`);

  const postFunc: MutationFunction<TResponse, IVariables> = ({
    pathParam,
    ...restParam
  }) => {
    return axiosClient.post(getPath(pathParam as never), restParam);
  };

  return { postFunc, key };
};

export const createPostHook = <TResponse, TVariables>(
  postFunc: MutationFunction<TResponse, TVariables>,
  path: IPostApiEntities,
) => {
  const usePost = createMutation<TResponse, TVariables>({
    mutationFn: postFunc,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`${path}.getAll`],
      });
      queryClient.invalidateQueries({
        queryKey: [`infinite.${path}.getAll`],
      });
    },
  });

  return { usePost };
};

export const createPostApi = <
  TResponse,
  TPathParams extends object | undefined,
  TData extends object | undefined,
>(
  path: IPostApiEntities,
) => {
  const { postFunc } = createPostFunc<TResponse, TPathParams, TData>(path);
  const { usePost } = createPostHook(postFunc, path);

  return { postFunc, usePost };
};
